/* eslint-disable no-unused-vars */
import axios from "axios";
import { useEffect, useState } from "react";

// ------------------
// Retry helper
// ------------------
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// check if response data is empty.
const isEmptyData = (data) => {
    if (data === null || data === undefined) return true;
    if (Array.isArray(data) && data.length === 0) return true;
    if (typeof data === "object" && Object.keys(data).length === 0) return true;
    return false;
};

// Create an instance of Axios for HTTP requests
const httpClient = axios.create({
    baseURL: process.env.REACT_APP_BASE_API,
});

// ------------------
// URL builder
// ------------------
const getUrl = ({ endpoint, params, queryParams }) => {
    const url = new URL(endpoint, process.env.REACT_APP_BASE_API);

    if (params) {
        Object.entries(params).forEach(([key, value]) => {
            if (url.pathname.includes(key)) {
                url.pathname = url.pathname.replace(`:${key}`, value);
            } else {
                url.pathname += `/${value}`;
            }
        });
    }

    // Add query parameters to the URL
    if (queryParams) {
        Object.entries(queryParams).forEach(([key, value]) => {
            url.searchParams.append(key, value);
        });
    }

    return url.toString();
};

// ------------------
// useHttp Hook
// ------------------
const useHttp = () => {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        // -------- Request interceptor --------
        const requestInterceptor = httpClient.interceptors.request.use(
            (config) => {
                setIsLoading(true);

                config.headers = {
                    Accept: "application/json, text/plain, */*",
                    "Content-Type": "multipart/form-data",
                };

                // Example auth
                // config.headers.Authorization = `Bearer ${token || localStorage.getItem("pp-token")}`;

                return config;
            },
            (errorObj) => {
                setIsLoading(false);
                return Promise.reject(errorObj);
            }
        );

        // -------- Response interceptor (RETRY LOGIC HERE) --------
        const responseInterceptor = httpClient.interceptors.response.use(
            async(response) => {
                setIsLoading(false);
                const config = response.config;
                // 👇 FORCE retry if success but empty data
                if (config?.retry && isEmptyData(response.data)) {
                    config.__retryCount = config.__retryCount || 0;

                    if (config.__retryCount < config.retry) {
                        config.__retryCount += 1;

                        const delay =
                            typeof config.retryDelay === "function"
                                ? config.retryDelay(config.__retryCount)
                                : config.retryDelay || 500;

                        await sleep(delay);

                        return httpClient(config);
                    }
                }
                return response;
            },
            async (errorObj) => {
                setIsLoading(false);

                const config = errorObj.config;

                // Retry only if enabled
                if (config && config.retry) {
                    config.__retryCount = config.__retryCount || 0;

                    // Retry only network errors / 5xx
                    const status = errorObj?.response?.status;
                    const shouldRetry = status;
                    if (
                        shouldRetry &&
                        config.__retryCount < config.retry
                    ) {
                        config.__retryCount += 1;

                        const delay =
                            typeof config.retryDelay === "function"
                                ? config.retryDelay(config.__retryCount)
                                : config.retryDelay || 500;

                        await sleep(delay);

                        return httpClient(config);
                    }
                }

                // Auth error handling
                if (errorObj.response?.status === 401) {
                    try {
                        // logout();
                    } catch (err) {
                        setError(err);
                    }
                } else {
                    setError(errorObj);
                }

                return Promise.reject(errorObj);
            }
        );

        // Cleanup (React 18 safe)
        return () => {
            httpClient.interceptors.request.eject(requestInterceptor);
            httpClient.interceptors.response.eject(responseInterceptor);
        };
    }, []);

    // ------------------
    // HTTP methods
    // ------------------

    const get = async ({ endpoint, params, queryParams, retry = 3 }) => {
        const url = getUrl({ endpoint, params, queryParams });

        const response = await httpClient.get(url, {
            retry,
            retryDelay: (count) => count * 500,
        });

        setData(response.data);
        return response.data;
    };

    const post = async ({ endpoint, payload, params, retry = 3 }) => {
        const url = getUrl({ endpoint, params });

        const response = await httpClient.post(url, payload, {
            retry,
            retryDelay: (count) => count * 500,
        });

        setData(response.data);
        return response.data;
    };

    const put = async ({ endpoint, payload, params, retry = 3 }) => {
        const url = getUrl({ endpoint, params });

        const response = await httpClient.put(url, payload, {
            retry,
            retryDelay: (count) => count * 500,
        });

        setData(response.data);
        return response.data;
    };

    const patch = async ({ endpoint, payload, params, retry = 3 }) => {
        const url = getUrl({ endpoint, params });

        const response = await httpClient.patch(url, payload, {
            retry,
            retryDelay: (count) => count * 500,
        });

        setData(response.data);
        return response.data;
    };

    const del = async ({ endpoint, retry = 3 }) => {
        const url = getUrl({ endpoint });

        const response = await httpClient.delete(url, {
            retry,
            retryDelay: (count) => count * 500,
        });

        setData(null);
        return response;
    };

    return {
        data,
        isLoading,
        error,
        get,
        post,
        put,
        patch,
        delete: del,
    };
};

export default useHttp;
